import type {
  CursorPaginatedResponse,
  UnionAgreement,
  UnionAgreementListParams,
  UnionAgreementPayload,
} from '@/libs/interfaces'

import { mockUnionAgreements } from '../fixtures/unionAgreements'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const unionAgreementStore = createEntityStore<UnionAgreement, 'unionAgreementID'>(
  mockUnionAgreements,
  'unionAgreementID',
)

const filterUnionAgreements = (params: UnionAgreementListParams): UnionAgreement[] =>
  unionAgreementStore.find((agreement) => matchesSearch(agreement, params.search, ['name']))

export const unionAgreementsApiMock = {
  create: async (orgID: string, payload: UnionAgreementPayload): Promise<UnionAgreement> => {
    const unionAgreement: UnionAgreement = {
      unionAgreementID: genID('union-agreement'),
      orgID,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    unionAgreementStore.insert(unionAgreement)
    return delay(unionAgreement)
  },

  getByID: async (_orgID: string, id: string): Promise<UnionAgreement> =>
    delay(unionAgreementStore.getById(id) ?? ({} as UnionAgreement)),

  update: async (
    _orgID: string,
    id: string,
    payload: Partial<UnionAgreementPayload>,
  ): Promise<UnionAgreement> => {
    const updated = unionAgreementStore.update(id, {
      ...payload,
      updatedAt: nowISO(),
    })
    return delay(updated ?? ({} as UnionAgreement))
  },

  delete: async (_orgID: string, id: string): Promise<void> => {
    unionAgreementStore.remove(id)
    await delay(undefined)
  },

  list: async (_orgID: string): Promise<UnionAgreement[]> => delay(unionAgreementStore.getAll()),

  listCursor: async (
    params: UnionAgreementListParams,
  ): Promise<CursorPaginatedResponse<UnionAgreement>> =>
    delay(paginate(filterUnionAgreements(params), params)),
}
